import styled from 'styled-components';
import { CustomLabelInput } from '../../../../../components/input/CustomLabelInput';
import { useSettingVariablesActionsContextHook, useSettingVariablesValueContextHook } from '../../contexts/SettingVariablesContext';
import CustomBlockButton from '../../../../../components/button/CustomBlockButton';
import { CustomDateUtils } from '../../../../../utils/CustomDateUtils';
import { useEffect, useState } from 'react';
import { GlobalWegetDateSelectorModal } from '../../../../../global-wegets/global-weget-date-selector-modal';
import CustomSelect from '../../../../../components/select/default/v1/CustomSelect';
import { useApiInfoContextValueHook } from '../../../../../contexts/ApiInfoContext';
import { SellertoolSignatureUtils } from '../../../../../utils/sellertool-signature-utils';
import { ProductCategoriesApi } from '../../../../../apis/ProductCategoriesApi';
import { useProductCategoriesActionsContextHook, useProductCategoriesValueContextHook } from '../../contexts/ProductCategoriesContext';
import { ModalProductCategorySelector } from './modals/modal-product-category-selector';
import moment from 'moment';
import { AnalyticsApi } from '../../../../../apis/AnalyticsApi';
import { customToast } from '../../../../../components/toast/custom-react-toastify/v1';
import { CustomBackdropController } from '../../../../../components/backdrop/default/v1';
import { useStockCycleDatasActionsContextHook } from '../../contexts/StockCycleDatasContext';
import { useCustomFetcher } from '../../fetcher/useCustomFetcher';
import { FormControlLabel, Switch } from '@mui/material';
import { LucideShoppingBag, LucideShoppingCart, LucideX } from 'lucide-react';
import { ModalShoppingCartStages } from './modals/modal-shopping-cart-stages';
import { useSelectedShoppingCartStageActionsContextHook, useSelectedShoppingCartStageValueContextHook } from '../../contexts/SelectedShoppingCartStageContext';
import { ModalShoppingCartItems } from './modals/modal-shopping-cart-items/ModalShoppingCartItems';
import CustomSmallButton from '../../../../../components/button/CustomSmallButton/CustomSmallButton';
import { useAnalysisVersionValueContextHook } from '../../contexts/AnalysisVersionContext';
import { ANALYSIS_VERSIONS } from '../../variables/analysisVersions';

const StyledContainer = styled.div`
    padding: 10px;
`;

const StyledWrapper = styled.div`
    border: 1px solid #e0e0e0;
    border-radius: 3px;
    background: #fff;
    padding: 10px;
    margin-bottom: 10px;

    .small-button{
        width: fit-content;
        padding: 0 16px;
        height: 30px;
        border-radius: 3px;
        font-size: 12px;
        background-color: #333;
        color:#fff;
        font-weight: 700;
        border:none;
    }
`;

const StyledFormGroups = styled.form`
    display: flex;
    flex-direction: column;
    gap: 20px;
    .form-group {
        .label{
            font-size: 14px;
            color: #333;
            font-weight: 700;
            margin-bottom: 5px;
        }

        .flexible{
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .input-box{
            width: 200px;
            input{
                border-color: #e0e0e0;
                border-radius: 3px;

                &:focus{
                    border-color: #333333;
                    box-shadow: 0 0 0 0.2rem rgba(0, 0, 0, 0.25);
                }
            }

            button{
                border-radius: 3px;
                border-color: #333;
                font-weight: 600;
                color: #333;
            }

            select{
                border-radius: 3px;
            }
        }

        .selected-category-badge{
            padding: 5px 10px;
            background-color: #f0f0f0;
            border-radius: 3px;
            font-size: 12px;
        }
    }

    .bottom{
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        button{
            width: 100px;
            height: 40px;
            font-size: 14px;
            font-weight: 700;
            color: #fff;
            background-color: #333;
            border-radius: 3px;
            border: none;

            &.reset{
                background-color: #efefef;
                color:#333;
            }
        }
    }
`;


export function WegetSettingVariables(props) {
    const apiInfoContextValueHook = useApiInfoContextValueHook();

    const settingVariablesValueContextHook = useSettingVariablesValueContextHook();
    const settingVariablesActionsContextHook = useSettingVariablesActionsContextHook();
    const settingVariables = settingVariablesValueContextHook.settingVariables;

    const productCategoriesValueContextHook = useProductCategoriesValueContextHook();
    const productCategoriesActionsContextHook = useProductCategoriesActionsContextHook();
    const selectedProductCategories = productCategoriesValueContextHook?.productCategories?.filter(item => settingVariables?.productCategoryIds?.some(r => r === item?.id));

    const analysisVersionValueContextHook = useAnalysisVersionValueContextHook();
    const analysisVersion = analysisVersionValueContextHook.analysisVersion;
    const isV3 = analysisVersion === ANALYSIS_VERSIONS.V3;

    const selectedShoppingCartStageValueContextHook = useSelectedShoppingCartStageValueContextHook();
    const selectedShoppingCartStageActionsContextHook = useSelectedShoppingCartStageActionsContextHook();
    const selectedShoppingCartStage = selectedShoppingCartStageValueContextHook?.selectedShoppingCartStage

    const [releaseRangeSelectorOpen, setReleaseRangeSelectorOpen] = useState(false);
    const [productCategorySelectorModalOpen, setProductCategorySelectorModalOpen] = useState(false);

    const [shoppingCartStagesModalOpen, setShoppingCartStagesModalOpen] = useState(false);

    const [shoppingCartItemsModalOpen, setShoppingCartItemsModalOpen] = useState(false);

    const customFetcher = useCustomFetcher();

    useEffect(() => {
        if (!apiInfoContextValueHook?.apiInfo?.apiKey || !apiInfoContextValueHook?.apiInfo?.secretKey) {
            return;
        }
        handleFetchProductCategories();
    }, [
        apiInfoContextValueHook?.apiInfo?.apiKey,
        apiInfoContextValueHook?.apiInfo?.secretKey,
    ])

    const handleFetchProductCategories = async () => {
        const timestamp = Date.now().toString();
        const signature = SellertoolSignatureUtils().generateSigniture({
            apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
            secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
            timestamp: timestamp
        })

        const headers = {
            'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
            'x-sellertool-timestamp': timestamp,
            'x-sellertool-signiture': signature
        }

        const response = await ProductCategoriesApi().searchList({
            headers: headers
        })

        if (response?.content) {
            productCategoriesActionsContextHook.productCategories.onSet(response?.content);
        }
    }

    const handleFetchStockCycleDatas = async () => {
        CustomBackdropController().showBackdrop();
        await customFetcher.onFetchStockCycleDatas(settingVariables, analysisVersion);
        await customFetcher.onFetchAlreadyPurchasedDatas()
        CustomBackdropController().hideBackdrop();
    }

    return (
        <>
            <StyledContainer>
                <StyledWrapper>
                    <StyledFormGroups onSubmit={(e) => { e.preventDefault(); handleFetchStockCycleDatas() }}>
                        <div className='form-group'>
                            <div className='label'>카테고리 선택</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div className='input-box'>
                                    <CustomBlockButton
                                        type='button'
                                        onClick={() => setProductCategorySelectorModalOpen(true)}
                                    >
                                        선택
                                    </CustomBlockButton>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', maxWidth: '300px' }}>
                                    {selectedProductCategories?.map(item => {
                                        return (
                                            <div key={item?.id} className='selected-category-badge'>
                                                {item?.name}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className='form-group'>
                            <div className='label'>검색 조건</div>
                            <div className='flexible'>
                                <div className='input-box'>
                                    <CustomSelect value={settingVariables?.searchCondition || ''} onChange={(e) => {
                                        settingVariablesActionsContextHook.settingVariables.setValue(prev => {
                                            return {
                                                ...prev,
                                                searchCondition: e.target.value
                                            }
                                        })
                                    }}>
                                        <option value=''>
                                            선택
                                        </option>
                                        <option value='PRODUCT_NAME'>
                                            상품명
                                        </option>
                                        <option value='OPTION_CODE'>
                                            옵션코드
                                        </option>
                                        <option value='PRODUCT_TAG'>
                                            상품태그
                                        </option>
                                        <option value='OPTION_TAG'>
                                            옵션태그
                                        </option>
                                    </CustomSelect>
                                </div>
                                <div className='input-box'>
                                    <CustomLabelInput>
                                        <CustomLabelInput.Input
                                            type='text'
                                            value={settingVariables?.searchQuery || ''}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                settingVariablesActionsContextHook.settingVariables.setValue(prev => {
                                                    return {
                                                        ...prev,
                                                        searchQuery: value
                                                    }
                                                })
                                            }}
                                        />
                                        <CustomLabelInput.Label>검색어</CustomLabelInput.Label>
                                    </CustomLabelInput>
                                </div>
                            </div>
                        </div>
                        <div className='form-group'>
                            <div className='label'>분석 변수</div>
                            <div className='flexible'>
                                <div className='input-box'>
                                    <CustomBlockButton
                                        type='button'
                                        onClick={() => setReleaseRangeSelectorOpen(true)}
                                    >
                                        <div style={{ fontSize: '11px', marginBottom: '5px', color: '#606060' }}>분석 기간 ({moment(settingVariables?.releaseTo).diff(settingVariables?.releaseFrom, "days") + 1}일)</div>
                                        <div>
                                            {CustomDateUtils().dateToYYYYMMDD(settingVariables?.releaseFrom)} ~ {CustomDateUtils().dateToYYYYMMDD(settingVariables?.releaseTo)}
                                        </div>
                                    </CustomBlockButton>
                                </div>
                                <div className='input-box'>
                                    <CustomLabelInput>
                                        <CustomLabelInput.Input
                                            type='number'
                                            value={settingVariables?.targetDOS || ''}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                settingVariablesActionsContextHook.settingVariables.setValue(prev => {
                                                    return {
                                                        ...prev,
                                                        targetDOS: value
                                                    }
                                                })
                                            }}
                                        />
                                        <CustomLabelInput.Label>목표 재고소진 기간 (일)</CustomLabelInput.Label>
                                    </CustomLabelInput>
                                </div>
                                <div className='input-box'>
                                    <CustomLabelInput>
                                        <CustomLabelInput.Input
                                            type='number'
                                            value={settingVariables?.daysOfLeadTime || ''}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                settingVariablesActionsContextHook.settingVariables.setValue(prev => {
                                                    return {
                                                        ...prev,
                                                        daysOfLeadTime: value
                                                    }
                                                })
                                            }}
                                        />
                                        <CustomLabelInput.Label>리드타임 (일)</CustomLabelInput.Label>
                                    </CustomLabelInput>
                                </div>
                            </div>
                        </div>
                        {isV3 &&
                            <div className='form-group'>
                                <div className='label'>이상치 판정 배수 (IQR V3)</div>
                                <div className='flexible'>
                                    {V3_MULTIPLIER_FIELDS?.map(field => {
                                        return (
                                            <div className='input-box' key={field.key}>
                                                <CustomLabelInput>
                                                    <CustomLabelInput.Input
                                                        type='number'
                                                        step='any'
                                                        value={settingVariables?.[field.key] ?? ''}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            settingVariablesActionsContextHook.settingVariables.setValue(prev => {
                                                                return {
                                                                    ...prev,
                                                                    [field.key]: value
                                                                }
                                                            })
                                                        }}
                                                    />
                                                    <CustomLabelInput.Label>{field.name}</CustomLabelInput.Label>
                                                </CustomLabelInput>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div style={{ marginTop: '5px', fontSize: '11px', color: '#909090' }}>
                                    상한 = max(중앙값 배수 × 건당수량 중앙값, P90 배수 × 건당수량 P90, 일평균 배수 × 일평균 출고량)
                                </div>
                            </div>
                        }
                        <div className='bottom'>
                            <CustomBlockButton
                                type='submit'
                            >
                                분석
                            </CustomBlockButton>
                        </div>
                    </StyledFormGroups>
                </StyledWrapper>
                <StyledWrapper>
                    <div>
                        <FormControlLabel control={<Switch checked={settingVariables?.viewOnlyUnderLeadTimeFlag} onChange={(e) => {
                            settingVariablesActionsContextHook.settingVariables.setValue(prev => {
                                return {
                                    ...prev,
                                    viewOnlyUnderLeadTimeFlag: e.target.checked
                                }
                            })
                        }} />} label={<span style={{ fontSize: 14, fontWeight: 600 }}>리드타임 이하만 보기</span>} />
                    </div>
                    <div>
                        <FormControlLabel control={<Switch checked={settingVariables?.hideNoOrderFlag} onChange={(e) => {
                            settingVariablesActionsContextHook.settingVariables.setValue(prev => {
                                return {
                                    ...prev,
                                    hideNoOrderFlag: e.target.checked
                                }
                            })
                        }} />} label={<span style={{ fontSize: 14, fontWeight: 600 }}>미주문건 가리기</span>} />
                    </div>
                </StyledWrapper>
                <StyledWrapper style={{ display: 'flex', alignItems: 'center', gap: '10px', minHeight: '40px', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <CustomSmallButton
                            type='button'
                            className='bg-gray-200 text-gray-800 border border-gray-800'
                            onClick={() => setShoppingCartStagesModalOpen(true)}
                        >
                            <LucideShoppingCart size={20} />
                        </CustomSmallButton>
                        {selectedShoppingCartStage &&
                            <>
                                <div style={{ fontSize: '12px', color: '#606060' }}>
                                    <div>선택된 장바구니</div>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#333' }}>{selectedShoppingCartStage?.name}</div>
                                </div>
                                <LucideX
                                    size={24}
                                    color='#666666'
                                    cursor={'pointer'}
                                    onClick={() => selectedShoppingCartStageActionsContextHook.selectedShoppingCartStage.onSet(null)}
                                />
                            </>
                        }
                    </div>
                    <div>
                        <CustomBlockButton
                            type='button'
                            className='small-button'
                            onClick={() => setShoppingCartItemsModalOpen(true)}
                        >
                            리스트
                        </CustomBlockButton>
                    </div>
                </StyledWrapper>
            </StyledContainer>

            {releaseRangeSelectorOpen &&
                <GlobalWegetDateSelectorModal
                    open={releaseRangeSelectorOpen}
                    onClose={() => setReleaseRangeSelectorOpen(false)}
                    initialStartDateTime={new Date(settingVariables?.releaseFrom)}
                    initialEndDateTime={new Date(settingVariables?.releaseTo)}
                    onAccept={(val) => {
                        settingVariablesActionsContextHook.settingVariables?.setValue(prev => {
                            return {
                                ...prev,
                                releaseFrom: val?.startDate,
                                releaseTo: val?.endDate
                            }
                        });
                        setReleaseRangeSelectorOpen(false)
                    }}
                />
            }

            {productCategorySelectorModalOpen &&
                <ModalProductCategorySelector
                    open={productCategorySelectorModalOpen}
                    onClose={() => setProductCategorySelectorModalOpen(false)}
                />
            }

            {shoppingCartStagesModalOpen &&
                <ModalShoppingCartStages
                    open={shoppingCartStagesModalOpen}
                    onClose={() => setShoppingCartStagesModalOpen(false)}
                />
            }

            {shoppingCartItemsModalOpen &&
                <ModalShoppingCartItems
                    open={shoppingCartItemsModalOpen}
                    onClose={() => setShoppingCartItemsModalOpen(false)}
                />
            }
        </>
    );
}

const V3_MULTIPLIER_FIELDS = [
    { key: 'orderQtyMultiplier', name: '건당수량 중앙값 배수' },
    { key: 'highQuantileMultiplier', name: '건당수량 P90 배수' },
    { key: 'dailyRateMultiplier', name: '일평균 출고량 배수' }
]
